import { useEffect, useRef, useState } from "react";
import { ModalShell } from "../../ModalShell";
import { EyeIcon, EyeOffIcon } from "../icons/EyeIcons";
import type { AdminSecureNote, AdminSecureNotesResult, RevealedSecureNote } from "../../../hooks/useAdminSecureNotes";
import dialogStyles from "../../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./SecureNoteModals.module.css";

type RevealSecureNoteModalProps = {
  note: AdminSecureNote;
  reveal: (id: string) => Promise<AdminSecureNotesResult<RevealedSecureNote>>;
  onClose: () => void;
};

// Admin-only. The reveal POST fires once on open and is recorded in the
// audit trail server-side; the value lives only in this component's state
// and is gone on close — never cached or persisted.
export const RevealSecureNoteModal = ({ note, reveal, onClose }: RevealSecureNoteModalProps): JSX.Element => {
  const [value, setValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [copyError, setCopyError] = useState<boolean>(false);
  // The POST has an audited side effect, so it must fire exactly once per
  // open — this guard also keeps StrictMode's dev-mode double effect from
  // writing two SECURE_NOTE_REVEALED rows.
  const revealFired = useRef<boolean>(false);

  useEffect(() => {
    if (revealFired.current) return;
    revealFired.current = true;
    void reveal(note.id).then((result) => {
      if (result.ok) setValue(result.value.value);
      else setError(result.message);
    });
  }, [note.id, reveal]);

  const handleCopy = async (): Promise<void> => {
    if (value === null) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setCopyError(false);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Blurred window (NotAllowedError) or no clipboard API — tell the
      // admin instead of letting them paste a stale clipboard.
      setCopied(false);
      setCopyError(true);
    }
  };

  const titleId = "reveal-secure-note-title";

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId} size="sm" width="min(520px, 92vw)">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>{note.name}</h3>
        <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <div className={styles.form}>
        <p className={styles.hint}>
          Shared by <strong>{note.ownerEmail}</strong>. This reveal is recorded in the audit trail.
        </p>

        {value === null && error === null && <div className={styles.loading}>Decrypting…</div>}
        {error !== null && <p className={styles.errorBanner}>{error}</p>}

        {value !== null && (
          <div className={styles.field}>
            <div className={styles.valueWrap}>
              <textarea
                className={show ? styles.valueArea : `${styles.valueArea} ${styles.valueMasked}`}
                value={value}
                readOnly
                spellCheck={false}
                data-testid="revealed-secure-note-value"
              />
              <button
                type="button"
                className={styles.revealBtn}
                onClick={() => setShow((prev) => !prev)}
                aria-label={show ? "Hide secret value" : "Show secret value"}
                aria-pressed={show}
                tabIndex={-1}
              >
                {show ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
        )}

        {copyError && (
          <p className={styles.errorBanner}>
            Copy failed — reveal the value with the eye icon and copy it manually.
          </p>
        )}

        <footer className={styles.footer}>
          {copied && <span className={styles.copiedTag}>Copied</span>}
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Done
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => void handleCopy()}
            disabled={value === null}
          >
            Copy value
          </button>
        </footer>
      </div>
    </ModalShell>
  );
};
